import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Workflow,
  HelpCircle,
  BarChart2,
  Terminal,
  UserPlus,
  TreePalm,
  LayoutDashboard,
  Rss,
  MessageSquareText,
  Radar,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import AnalyticsPanel from './AnalyticsPanel';
import { useIsMobile } from '../helpers/useIsMobile';
import styles from './SessionNavToolbar.module.css';

interface SessionNavToolbarProps {
  side: 'left' | 'right';
}

export const SessionNavToolbar: React.FC<SessionNavToolbarProps> = ({ side }) => {
  const isMobile = useIsMobile();
  const [showAnalytics, setShowAnalytics] = useState(false);
  const tooltipSide = side === 'left' ? 'right' : 'left';
  const navigate = useNavigate();
  const handleTaskingBotTap = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      navigate('/taskingbot');
    },
    [navigate],
  );

  if (isMobile) return null;

  return (
    <>
      <div className={`${styles.toolbar} ${side === 'left' ? styles.left : styles.right}`}>
        <div className={styles.navSection}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/dashboard" className={styles.navButton}>
                <LayoutDashboard size={16} />
              </Link>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>Dashboard</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/workflows" className={styles.navButton}>
                <Workflow size={16} />
              </Link>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>Workflows</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/taskingbot" className={styles.navButton} onTouchEnd={handleTaskingBotTap}>
                <img src="/taskingbot.png" alt="TaskingBot" className={styles.taskingbotAvatar} />
              </Link>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>TaskingBot</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/taskingbot/board" className={styles.navButton}>
                <Radar size={16} />
              </Link>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>TaskFlow</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => window.dispatchEvent(new CustomEvent('tasking.openAiChat'))}
              >
                <MessageSquareText size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>AI Chat</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/news" className={styles.navButton}>
                <Rss size={16} />
              </Link>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>Feed</TooltipContent>
          </Tooltip>
        </div>

        <div className={styles.divider} />

        <div className={styles.navSection}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={styles.navButton}
                onClick={() => window.dispatchEvent(new CustomEvent('tasking.openGitTree'))}
              >
                <TreePalm size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>Git Repos</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/faq" className={styles.navButton}>
                <HelpCircle size={16} />
              </Link>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>FAQ</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button className={styles.navButton} onClick={() => setShowAnalytics(true)}>
                <BarChart2 size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>Open Analytics</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={styles.navButton}
                onClick={() => window.dispatchEvent(new CustomEvent('tasking.openTerminal'))}
              >
                <Terminal size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>Terminal</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={styles.navButton}
                onClick={() => window.dispatchEvent(new CustomEvent('tasking.openInvite'))}
              >
                <UserPlus size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>Invite Participant</TooltipContent>
          </Tooltip>
        </div>
      </div>
      {showAnalytics && <AnalyticsPanel onClose={() => setShowAnalytics(false)} />}
    </>
  );
};
